import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  `inline-flex items-center justify-center font-sans font-bold text-[16px] leading-[25px]
  tracking-[0.32px] rounded-[6px] no-underline
  enabled:cursor-pointer disabled:opacity-30 text-center`,
  {
    variants: {
      variant: {
        default:
          "border border-violet-700 shadow-[0px_1px_4px_rgba(0,0,0,0.4)] bg-linear-to-b from-purple-500 to-violet-500 text-white enabled:hover:from-[#4a2cd6] enabled:hover:to-[#3f29a6] enabled:active:from-[#3a2199] enabled:active:to-[#3a2199]",
        secondary:
          "bg-violet-700 border border-white text-white rounded-[6px] px-[30px] py-[10px] font-bold text-[16px] leading-[25px] tracking-[0.32px] hover:bg-violet-800",
        yellow:
          "border border-yellow shadow-[0px_1px_4px_rgba(0,0,0,0.4)] bg-linear-to-b from-yellow to-[#f8e439] text-jaguar-900 enabled:hover:from-[#f5df0f] enabled:hover:to-[#edd82e] enabled:active:from-[#e6d520] enabled:active:to-[#e6d520]",
        cta:
          "border border-violet-700 shadow-[0px_1px_4px_rgba(0,0,0,0.4)] bg-linear-to-b from-purple-500 to-violet-500 text-white tracking-[0.3px] enabled:hover:from-[#4a2cd6] enabled:hover:to-[#3f29a6] enabled:active:from-[#3a2199] enabled:active:to-[#3a2199]",
        nav:
          "bg-off-white text-violet-700 font-normal text-[15px] tracking-[0.3px] hover:bg-violet-100 hover:text-jaguar-900 aria-[current=page]:bg-off-white aria-[current=page]:text-jaguar-900 aria-[current=page]:font-bold!",
        outline:
          "border border-violet-700 shadow-[0px_1px_4px_rgba(0,0,0,0.4)] bg-white text-violet-700 enabled:hover:bg-violet-100 enabled:hover:text-jaguar-900 enabled:active:bg-violet-200",
        link:
          "text-white/80 text-[15px] tracking-[0.3px] hover:text-yellow hover:underline",

      },
      size: {
        default: "px-[30px] py-[10px]",
        sm: "px-[18px] py-[6px]",
        lg: "px-7 py-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
